import type { OnboardingFeaturedTemplateRef } from "@/constants/onboardingFeaturedTemplates";
import type { TripTemplate } from "@/types/tripTemplates";
import { isTemplateTopicTag, normalizeTemplateTagInput } from "@/utils/templateTags";

type TripTemplateWithTopicTags = TripTemplate & { topicTags?: string[] };

const TITLE_HINTS: Record<string, string[]> = {
  "world-cup-2026": ["world cup"],
  "calgary-stampede": ["stampede"],
  "america-250": ["america 250"],
};

export function templateSlugFromTitle(title: string): string {
  return normalizeTemplateTagInput(String(title || "").replace(/[^\w\s-]/g, ""));
}

export function getTemplateTagList(template: TripTemplate): string[] {
  const withTopicTags = template as TripTemplateWithTopicTags;
  const raw = [...(withTopicTags.topicTags ?? []), ...(template.tags ?? [])];
  const travelStyle = new Set(
    (template.travelStyle ?? []).map((style) => String(style).trim().toLowerCase()),
  );
  const seen = new Set<string>();
  const tags: string[] = [];

  for (const tag of raw) {
    const normalized = String(tag || "").trim().toLowerCase();
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    if (!isTemplateTopicTag(normalized, travelStyle)) {
      continue;
    }
    seen.add(normalized);
    tags.push(normalized);
  }

  return tags;
}

export function templateHasTopicTag(template: TripTemplate, topicTag: string): boolean {
  const normalized = topicTag.trim().toLowerCase();
  return getTemplateTagList(template).includes(normalized);
}

export function templateMatchesSlug(template: TripTemplate, slug: string): boolean {
  const computed = templateSlugFromTitle(template.title);
  const target = slug.trim().toLowerCase();
  if (!computed || !target) {
    return false;
  }
  return computed === target || computed.startsWith(target) || target.startsWith(computed);
}

export function templateMatchesTitleHint(template: TripTemplate, topicTag: string): boolean {
  const hints = TITLE_HINTS[topicTag.toLowerCase()];
  if (!hints?.length) {
    return false;
  }
  const title = template.title.toLowerCase();
  return hints.some((hint) => title.includes(hint));
}

export function templateMatchesFeaturedRef(
  template: TripTemplate,
  ref: OnboardingFeaturedTemplateRef,
): boolean {
  return (
    template.id === ref.id ||
    templateMatchesSlug(template, ref.slug) ||
    templateHasTopicTag(template, ref.topicTag) ||
    templateMatchesTitleHint(template, ref.topicTag)
  );
}

export function compareTemplatePreference(a: TripTemplate, b: TripTemplate): number {
  if (a.featured !== b.featured) {
    return a.featured ? -1 : 1;
  }
  return (b.usageCount ?? 0) - (a.usageCount ?? 0);
}

export function pickBestTemplateForFeaturedRef(
  catalog: TripTemplate[],
  ref: OnboardingFeaturedTemplateRef,
  excludeIds: Set<string>,
): TripTemplate | null {
  const candidates = catalog.filter((template) => !excludeIds.has(template.id));
  const byId = candidates.find((template) => template.id === ref.id);
  if (byId) {
    return byId;
  }

  const bySlug = candidates.find((template) => templateMatchesSlug(template, ref.slug));
  if (bySlug) {
    return bySlug;
  }

  const topicMatches = candidates
    .filter(
      (template) =>
        templateHasTopicTag(template, ref.topicTag) ||
        templateMatchesTitleHint(template, ref.topicTag),
    )
    .sort(compareTemplatePreference);

  return topicMatches[0] ?? null;
}

export function mergeTemplateCatalog(
  primary: TripTemplate[],
  secondary: TripTemplate[],
): TripTemplate[] {
  const byId = new Map<string, TripTemplate>();
  for (const template of [...primary, ...secondary]) {
    if (!template?.id) {
      continue;
    }
    byId.set(template.id, template);
  }
  return Array.from(byId.values());
}
